import { IconProps } from '@scale/scaleui';
import { CommunityType, DiscourseCommunityLinks } from '@scale/shared/types/Community';
import { QualificationType } from '@scale/shared/types/qualification/types';
import { Colors } from 'remotasks/components/Expert/theme';

export const OUTLIER_NEXT_URL =
  process.env.NODE_ENV === 'development' ? 'http://localhost:3004' : 'https://app.outlier.ai';
export const OUTLIER_URL =
  process.env.NODE_ENV === 'development' ? 'http://localhost:3002' : 'https://app.outlier.ai';

export enum DashboardTodoCards {
  CompleteProfile = 'CompleteProfile',
  CompletedSecurityTraining = 'CompletedSecurityTraining',
  JoinedDiscourse = 'JoinedDiscourse',
  SetupBanking = 'Setup Banking',
  CompleteReferral = 'CompleteReferral',
}

interface IDashboardTodoCardContent {
  title: string;
  description: string;
  action: string;
  href: string;
  durationMin?: number;
  required?: boolean;
}

const getCompleteProfilePath = () => {
  if (typeof window === 'undefined') return '/expert/profile/complete';
  const value = localStorage.getItem(
    'unleash-internal/catalog/unleash/public/outlier-new-onboarding',
  );
  if (value === null) return '/expert/profile/complete';
  const newValue = JSON.parse(value);
  if (newValue === false) return '/expert/profile/complete';
  return '/expert/onboarding/worker-skills';
};

export const DashboardTodoCardContent: Record<DashboardTodoCards, IDashboardTodoCardContent> = {
  [DashboardTodoCards.CompleteProfile]: {
    title: 'Complete your Profile',
    description: `We ask you for a few details about yourself so we can match you with eligible tasks.`,
    action: 'Start',
    href: getCompleteProfilePath(),
    durationMin: 10,
    required: true,
  },
  [DashboardTodoCards.CompletedSecurityTraining]: {
    title: 'Take Security Onboarding',
    description:
      'Answer some questions to know the basic security concerns you need to consider to use Outlier.',
    action: 'Start',
    href: '/expert/course?id=645fdee37884141776646a37&returnTo=/expert',
    durationMin: 7,
  },
  [DashboardTodoCards.JoinedDiscourse]: {
    title: 'Join Outlier Forums',
    description: `Connect with project leaders and other taskers to get support with your tasks.`,
    action: 'Start',
    href: DiscourseCommunityLinks[CommunityType.OutlierDiscourse] as string,
    durationMin: 2,
  },
  [DashboardTodoCards.SetupBanking]: {
    title: 'Setup Banking',
    description: `Connect a payment method to receive secure payments for completed projects. You can also set this up later.`,
    action: 'Start',
    href: '/expert/profile/pay',
    durationMin: 2,
  },
  [DashboardTodoCards.CompleteReferral]: {
    title: 'Complete your referral',
    description: `Keep tasking to earn your referral bonus!`,
    action: 'Continue tasking',
    href: '/expert/tasks',
  },
};

export const DashboardIcons: Record<DashboardTodoCards, IconProps['icon']> = {
  [DashboardTodoCards.CompleteProfile]: 'clipboard-list',
  [DashboardTodoCards.CompletedSecurityTraining]: 'shield-alt',
  [DashboardTodoCards.JoinedDiscourse]: 'users',
  [DashboardTodoCards.SetupBanking]: 'piggy-bank',
  [DashboardTodoCards.CompleteReferral]: 'sack-dollar',
};

// render tooltip descriptions of a qualification section type
export const QualificationDescriptions: Partial<Record<QualificationType, string>> = {};

export const QualificationCardIcons = {
  /* -- QUALIFICATION TYPE CARD CODE GENERATOR -- */
  [QualificationType.DolphinExam]: {
    icon: 'clipboard-list',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.Codeforces]: {
    icon: 'code',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.HackerRank]: {
    icon: 'code',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.WorkerExperience]: {
    icon: 'work',
    color: Colors.Blue900,
    background: Colors.Blue900,
  },
  // todo: update this. placeholder for lint
  [QualificationType.AccessLink]: {
    icon: 'books',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.Acknowledgement]: {
    icon: 'tasks',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.Course]: {
    icon: 'books',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.Training]: {
    icon: 'books',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.InstructionItem]: {
    icon: 'books',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.WorkerSkill]: {
    icon: 'tasks',
    color: Colors.Green900,
    background: Colors.Green50,
  },
  [QualificationType.WFESignin]: {
    icon: 'sign-in',
    color: Colors.Green900,
    background: Colors.Green50,
  },
  [QualificationType.WFEStartTasking]: {
    icon: 'play',
    color: Colors.Green900,
    background: Colors.Green50,
  },
  [QualificationType.CompleteProfile]: {
    icon: 'clipboard-list',
    color: Colors.White,
    background: 'url(/static/images/projects-card-background.jpeg)',
  },
  [QualificationType.SecureTraining]: {
    icon: 'shield-alt',
    color: Colors.White,
    background: 'url(/static/images/projects-card-background.jpeg)',
  },
  [QualificationType.JoinSlack]: {
    icon: 'users',
    color: Colors.White,
    background: 'url(/static/images/projects-card-background.jpeg)',
  },
  [QualificationType.JoinCommunity]: {
    icon: 'users',
    color: Colors.White,
    background: 'url(/static/images/projects-card-background.jpeg)',
  },
  [QualificationType.AccountVerification]: {
    icon: 'video',
    color: Colors.White,
    background: 'url(/static/images/projects-card-background.jpeg)',
  },
  [QualificationType.WorkerProperty]: {
    icon: 'clipboard-list',
    color: Colors.White,
    background: 'url(/static/images/projects-card-background.jpeg)',
  },
  [QualificationType.Webinar]: {
    icon: 'laptop',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.ContributorAgreement]: {
    icon: 'pen',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.Tenure]: {
    icon: 'hourglass',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.ContributorQualityScore]: {
    icon: 'calculator',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.OnboardingSuccessRate]: {
    icon: 'check-double',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.IdvCheck]: {
    icon: 'id-card',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.CountryPermitted]: {
    icon: 'globe',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.NotTraveling]: {
    icon: 'passport',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.GoldCheck]: {
    icon: 'check',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.NonMarketplaceStatus]: {
    icon: 'users',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.Experiment]: {
    icon: 'flask',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.FraudBucket]: {
    icon: 'shield-alt',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.FraudCheckpoint]: {
    icon: 'shield-alt',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.Tag]: {
    icon: 'tag',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.InviteBatch]: {
    icon: 'envelope',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.LinkedInReachable]: {
    icon: 'check',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.LinkedInConnections]: {
    icon: 'users',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
  [QualificationType.Cohort]: {
    icon: 'users-between-lines',
    color: Colors.Blue900,
    background: Colors.Blue50,
  },
};

// cannot add these qualifications to qualification lists
export const UnaddableQualificationTypes: QualificationType[] = [];
